"use client";

import { useState } from "react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    FlipCard,
    FlipCardsAppearance,
    FlipCardsBlock as FlipCardsBlockType,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useResolvedGridGap } from "../../../hooks/useGridGap";
import {
    flipCardsVariant,
    resolveFlipAppearance,
} from "../../../lib/blockRegistry";
import { CompletionCheck } from "../shared/CompletionCheck";
import RichText from "../text/richText";
import RichHtml from "./richHtml";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";
import {
    CardFrame,
    CardIcon,
    CardIconPicker,
    cardFaceStyle,
    faceBorder,
    faceTextClass,
    gridClass,
    gridStyle,
    staticGridStyle,
} from "./flipCards/cardFace";
import { StaticEditCard, StaticPreviewCard } from "./flipCards/staticCard";

// ── Preview (learner) ──
function PreviewCard({
    card,
    index,
    appearance,
}: {
    card: FlipCard;
    index: number;
    appearance: FlipCardsAppearance;
}) {
    const locale = useLomdaLocale();
    const [flipped, setFlipped] = useState(false);
    const [seen, setSeen] = useState(false);
    const title =
        card.title ||
        t("lomda-v3-editor.flip.card-n", locale, { number: index + 1 });
    const showCheck = appearance.showCheckmark && seen;

    const flip = () => setFlipped(true);
    // Set on flip back, not flip: the front still shows while it turns away.
    const flipBack = () => {
        setFlipped(false);
        setSeen(true);
    };

    return (
        <div className="h-[200px] w-full" style={{ perspective: 1400 }}>
            <div
                className="relative h-full w-full transition-transform duration-500"
                style={{
                    transformStyle: "preserve-3d",
                    WebkitTransformStyle: "preserve-3d",
                    transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
            >
                <div
                    className={cn(
                        "absolute inset-0 flex flex-col justify-between overflow-hidden rounded-[20px] p-5",
                        faceTextClass(card)
                    )}
                    style={{
                        ...faceBorder(appearance),
                        ...cardFaceStyle(card, appearance),
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        opacity: flipped ? 0 : 1,
                        pointerEvents: flipped ? "none" : "auto",
                        transition: "opacity 0s linear 0.25s",
                    }}
                >
                    {showCheck && <CompletionCheck className="absolute end-5 top-5" />}
                    {card.icon && (
                        <CardIcon icon={card.icon} appearance={appearance} />
                    )}
                    <div className="mt-auto flex flex-col gap-2">
                        {/* A single long word would otherwise run outside the
                            card; clamping keeps the subtitle + Flip row on the
                            fixed-height face. */}
                        <h3
                            className={cn(
                                "lomda-v3-text-h5 line-clamp-3 break-words",
                                !card.title &&
                                    "text-[hsl(var(--lomda-muted-text))]"
                            )}
                            title={title}
                        >
                            {title}
                        </h3>
                        <div className="flex items-center justify-between gap-2">
                            {card.subtitle && (
                                <span
                                    className="lomda-v3-text-p line-clamp-2 min-w-0 break-words text-[hsl(var(--lomda-muted-text))]"
                                    title={card.subtitle}
                                >
                                    {card.subtitle}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={flip}
                                className="lomda-v3-text-p ms-auto text-foreground transition hover:text-primary"
                            >
                                {t("lomda-v3-editor.flip.flip", locale)}
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    className={cn(
                        "absolute inset-0 flex flex-col rounded-[20px] p-5",
                        faceTextClass(card)
                    )}
                    style={{
                        ...faceBorder(appearance),
                        ...cardFaceStyle(card, appearance),
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                        opacity: flipped ? 1 : 0,
                        pointerEvents: flipped ? "auto" : "none",
                        transition: "opacity 0s linear 0.25s",
                    }}
                >
                    <div className="flex min-h-0 flex-1 flex-col items-start overflow-y-auto">
                        <RichHtml
                            html={card.back}
                            className="lomda-v3-text-p my-auto w-full text-start"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={flipBack}
                        className="lomda-v3-text-p mt-2 self-end text-foreground transition hover:text-primary"
                    >
                        {t("lomda-v3-editor.flip.flip", locale)}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Editable card (canvas) ──
function EditCard({
    card,
    blockId,
    appearance,
    focused,
    onFocus,
    onPatch,
}: {
    card: FlipCard;
    blockId: string;
    appearance: FlipCardsAppearance;
    focused: boolean;
    onFocus: () => void;
    onPatch: (patch: Partial<FlipCard>) => void;
}) {
    const locale = useLomdaLocale();
    const [flipped, setFlipped] = useState(false);

    return (
        <CardFrame
            card={card}
            appearance={appearance}
            focused={focused}
            onFocus={onFocus}
            className="relative flex h-[200px] flex-col justify-between"
        >
            {appearance.showCheckmark && !flipped && (
                <CompletionCheck className="absolute end-5 top-5" />
            )}
            {flipped ? (
                <div className="flex h-full w-full min-h-0 flex-col">
                    <div className="flex min-h-0 flex-1 flex-col items-start overflow-y-auto [&>*]:my-auto [&>*]:w-full">
                        <RichText
                            initialHtml={card.back}
                            editable
                            useGlobalToolbar
                            blockId={blockId}
                            placeholder={t("lomda-v3-editor.flip.card-back-ph")}
                            onCommit={(back) => onPatch({ back })}
                            editorClassName="lomda-v3-text-p w-full text-start"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setFlipped(false)}
                        className="lomda-v3-text-p mt-2 self-end text-foreground transition hover:text-primary"
                    >
                        {t("lomda-v3-editor.flip.flip", locale)}
                    </button>
                </div>
            ) : (
                <>
                    <CardIconPicker
                        icon={card.icon}
                        appearance={appearance}
                        onPick={(icon) => onPatch({ icon })}
                    />

                    <div className="flex flex-col gap-2">
                        <input
                            value={card.title}
                            onChange={(e) => onPatch({ title: e.target.value })}
                            placeholder={t("lomda-v3-editor.flip.card-title-ph")}
                            className="lomda-v3-text-h5 w-full bg-transparent outline-none placeholder:text-[hsl(var(--lomda-muted-text))]"
                        />
                        <div className="flex items-center justify-between gap-2">
                            <input
                                value={card.subtitle}
                                onChange={(e) =>
                                    onPatch({ subtitle: e.target.value })
                                }
                                placeholder={t(
                                    "lomda-v3-editor.flip.card-description-ph"
                                )}
                                className="lomda-v3-text-p min-w-0 flex-1 bg-transparent text-[hsl(var(--lomda-muted-text))] outline-none placeholder:text-[hsl(var(--lomda-muted-text))]"
                            />
                            <button
                                type="button"
                                onClick={() => setFlipped(true)}
                                className="lomda-v3-text-p shrink-0 text-foreground transition hover:text-primary"
                            >
                                {t("lomda-v3-editor.flip.flip", locale)}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </CardFrame>
    );
}

export default function FlipCardsBlock({
    block,
}: {
    block: FlipCardsBlockType;
}) {
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);
    const setFocusedQuestion = useLomdaV3Editor((s) => s.setFocusedQuestion);
    const clearActiveEditor = useLomdaV3Editor((s) => s.clearActiveEditor);
    const focusedCardId = useLomdaV3Editor((s) => s.focusedQuestionId);

    const editable = useBlockEditable();
    const gridGap = useResolvedGridGap(block.id);
    const { cards } = block.data;
    const isStatic = flipCardsVariant(block.data) === "static";
    const columns = block.data.columns ?? 3;
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const appearance = resolveFlipAppearance(block.data.appearance, theme);
    const grid = isStatic
        ? { className: "grid", style: staticGridStyle(columns, gridGap) }
        : { className: gridClass, style: gridStyle(columns, gridGap) };

    const patchCard = (id: string, patch: Partial<FlipCard>) =>
        patchBlockData(block.id, "flip-cards", (d) => ({
            cards: d.cards.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        }));

    if (!editable) {
        const filled = cards.filter((c) =>
            isStatic
                ? c.icon || c.title.trim() || c.subtitle.trim()
                : c.icon || c.title || c.subtitle || c.back
        );
        if (filled.length === 0) return null;
        return (
            <div {...grid}>
                {filled.map((c, i) =>
                    isStatic ? (
                        <StaticPreviewCard
                            key={c.id}
                            card={c}
                            appearance={appearance}
                        />
                    ) : (
                        <PreviewCard
                            key={c.id}
                            card={c}
                            index={i}
                            appearance={appearance}
                        />
                    )
                )}
            </div>
        );
    }

    return (
        <div
            onPointerDownCapture={(e) => {
                setFocusedQuestion(null);
                if (
                    !(e.target as HTMLElement).closest?.(".lomda-v3-rich-text")
                ) {
                    clearActiveEditor();
                }
            }}
        >
            <div {...grid}>
                {cards.map((c) =>
                    isStatic ? (
                        <StaticEditCard
                            key={c.id}
                            card={c}
                            appearance={appearance}
                            focused={focusedCardId === c.id}
                            onFocus={() => setFocusedQuestion(c.id)}
                            onPatch={(patch) => patchCard(c.id, patch)}
                        />
                    ) : (
                        <EditCard
                            key={c.id}
                            card={c}
                            blockId={block.id}
                            appearance={appearance}
                            focused={focusedCardId === c.id}
                            onFocus={() => setFocusedQuestion(c.id)}
                            onPatch={(patch) => patchCard(c.id, patch)}
                        />
                    )
                )}
            </div>
        </div>
    );
}
